import { useCallback, useState } from 'react';

/**
 * A boolean that remembers its value in localStorage for this browser — used
 * for editor layout preferences (which side panels are collapsed) so the
 * choice survives reloads. A blocked or unavailable store just falls back to
 * `fallback` and forgets the change, never throwing.
 */
export function usePersistedBoolean(key: string, fallback: boolean): readonly [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? fallback : raw === 'true';
    } catch {
      return fallback;
    }
  });
  const set = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // A blocked store just forgets the choice on reload.
      }
    },
    [key],
  );
  return [value, set];
}
